import { useCallback, useEffect, useState } from 'react';
import { useBillingIa } from './useBillingIa';
import { portalBillingService } from '../services/portalBillingService';
import { subscribeBillingChanged } from '../services/billingEvents';
import type { BillingChecklistItemDto, BillingSummaryDto } from '../types/portalBilling';

export interface BillingState {
  /** True only under the new External IA (see `useBillingIa`). */
  enabled: boolean;
  /** Spendable analysis credits, clamped at 0 for display; 0 until loaded or when not enabled. */
  balance: number;
  /** The server's balance as-is (may be negative after a staff reopen, plan P3). */
  rawBalance: number;
  freeAnalysisUsed: boolean;
  profileComplete: boolean;
  isBusiness: boolean;
  checklist: BillingChecklistItemDto[];
  /** True while a summary fetch is in flight (always false when not enabled). */
  loading: boolean;
  /** True once a summary has been received (distinguishes "0 loaded" from "0 because unknown"). */
  loaded: boolean;
  /** Set when the last fetch failed (network or failure envelope); cleared by a successful refresh. */
  error: string | null;
  refresh: () => Promise<void>;
}

const EMPTY: BillingSummaryDto = {
  enabled: false,
  balance: 0,
  freeAnalysisUsed: false,
  profileComplete: false,
  isBusiness: false,
  checklist: [],
};

const UNAVAILABLE = 'Balance unavailable';

/**
 * Billing summary for the signed-in External user. Fetches `GET /api/portal/billing/summary` ONCE per
 * mount — and only under the new IA (`useBillingIa`). For everyone else it returns `enabled: false`
 * without touching the API, so staff/partner shells and the dark External shell make no billing calls.
 */
export function useBilling(): BillingState {
  const enabled = useBillingIa();

  const [summary, setSummary] = useState<BillingSummaryDto>(EMPTY);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (isCancelled: () => boolean) => {
    try {
      const res = await portalBillingService.getSummary();
      if (isCancelled()) return;
      if (res.success && res.data) {
        setSummary(res.data);
        setLoaded(true);
        setError(null);
      } else {
        setError(res.message || UNAVAILABLE);
      }
    } catch {
      // Best-effort: the shell never breaks because the balance could not be read.
      if (!isCancelled()) setError(UNAVAILABLE);
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    await load(() => false);
  }, [enabled, load]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    // Async IIFE (not a direct call) so every setState lands after an await, per the file-wide pattern.
    void (async () => {
      await load(() => cancelled);
    })();
    // Another instance changed the balance (purchase settled, credit consumed) — re-read ours.
    const unsubscribe = subscribeBillingChanged(() => {
      void load(() => cancelled);
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [enabled, load]);

  return {
    enabled,
    balance: enabled ? Math.max(0, summary.balance) : 0,
    rawBalance: enabled ? summary.balance : 0,
    freeAnalysisUsed: summary.freeAnalysisUsed,
    profileComplete: summary.profileComplete,
    isBusiness: summary.isBusiness,
    checklist: summary.checklist,
    loading: enabled ? loading : false,
    loaded,
    error: enabled ? error : null,
    refresh,
  };
}
